import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/icons/Icon';
import { cn } from '@/lib/cn';

/**
 * "Verify this pack" — the scratch-code sheet.
 *
 * §3.2: scratch-code verification stays OPTIONAL. It needs network and adds a
 * minute at the door, so it is offered here and never demanded — a buyer who
 * skips it still has the seal check and the batch check, which are the checks
 * that actually gate payment.
 *
 * A code that has been checked before is itself the signal (§7.6), so the
 * result screen distinguishes "genuine, never checked" from "genuine, but
 * someone has already checked this code" rather than collapsing both to a tick.
 */

export interface VerifyPackSheetProps {
  onClose: () => void;
  onVerify?: (code: string) => Promise<'genuine' | 'already_checked' | 'not_found'>;
}

const CODE_LENGTH = 12;

export function VerifyPackSheet({ onClose, onVerify }: VerifyPackSheetProps) {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<'genuine' | 'already_checked' | 'not_found' | null>(null);
  const [checking, setChecking] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes, and focus moves into the sheet: a bottom sheet that traps a
  // keyboard user behind it is worse than no sheet.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    dialogRef.current?.focus();
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const submit = async () => {
    if (code.length < CODE_LENGTH || !onVerify) return;

    setChecking(true);
    try {
      setResult(await onVerify(code));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 backdrop-blur-[1px]"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-title"
        tabIndex={-1}
        className="relative w-full max-w-[420px] rounded-t-[20px] bg-white px-[18px] pb-7 pt-2 outline-none sm:rounded-[20px]"
      >
        <div className="mx-auto mb-4 h-1 w-[38px] rounded-sm bg-line sm:hidden" />

        {result ? (
          <VerifyResult result={result} onDone={onClose} onRetry={() => setResult(null)} />
        ) : (
          <>
            <div className="mb-1 flex items-start justify-between gap-2">
              <h2
                id="verify-title"
                className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]"
              >
                Verify this pack
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-muted hover:bg-paper"
              >
                <Icon name="x" size={18} strokeWidth={1.7} />
              </button>
            </div>

            <p className="mb-[18px] text-xs text-muted">
              Scratch the silver panel on the back of the pack and enter or scan the code underneath.
            </p>

            <ScratchPanelArt />

            <label
              htmlFor="scratch-code"
              className="mb-[5px] block text-[11px] font-bold text-muted"
            >
              12-character code
            </label>
            <input
              id="scratch-code"
              value={code}
              onChange={(event) =>
                setCode(
                  event.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                    .slice(0, CODE_LENGTH),
                )
              }
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="————————————"
              className="mb-3 w-full rounded-inp border border-line bg-white px-3 py-3 font-mono text-[15px] tracking-[0.26em] text-ink outline-none placeholder:text-faint focus:border-blue focus:ring-2 focus:ring-blue/15"
            />

            <div className="my-3.5 flex items-center gap-2.5">
              <span className="h-px flex-1 bg-line-2" />
              <span className="text-[11px] text-faint">or</span>
              <span className="h-px flex-1 bg-line-2" />
            </div>

            <Button full variant="ghost" icon="scan" className="mb-2.5">
              Scan with camera
            </Button>
            <Button
              full
              loading={checking}
              disabled={code.length < CODE_LENGTH}
              onClick={submit}
            >
              Verify code
            </Button>

            <p className="mt-3.5 text-center text-[11px] font-semibold text-blue">
              Where do I find my scratch code?
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function VerifyResult({
  result,
  onDone,
  onRetry,
}: {
  result: 'genuine' | 'already_checked' | 'not_found';
  onDone: () => void;
  onRetry: () => void;
}) {
  const config = {
    genuine: {
      tone: 'verified' as const,
      title: 'Genuine pack',
      body: 'This code was issued once and has never been checked before.',
    },
    already_checked: {
      tone: 'caution' as const,
      title: 'This code has been used',
      body: 'The code is real, but someone has checked it before. If you just bought this pack, report it — a reused code can mean a copied carton.',
    },
    not_found: {
      tone: 'danger' as const,
      title: 'We do not recognise this code',
      body: 'Do not take this medicine. Report it now and we will refund you and escalate it to our counterfeit team.',
    },
  }[result];

  return (
    <div className="px-1 pb-2 pt-4 text-center">
      <div
        className={cn(
          'mx-auto mb-[18px] grid h-[76px] w-[76px] place-items-center rounded-full',
          config.tone === 'verified' && 'bg-verified-tint text-verified',
          config.tone === 'caution' && 'bg-caution-tint text-caution',
          config.tone === 'danger' && 'bg-danger-tint text-danger',
        )}
      >
        <Icon name={config.tone === 'verified' ? 'shield' : 'alert'} size={38} filled={config.tone === 'verified'} />
      </div>

      <h2
        className={cn(
          'm-0 font-display text-[22px] font-bold tracking-[-0.02em]',
          config.tone === 'verified' && 'text-verified',
          config.tone === 'caution' && 'text-caution',
          config.tone === 'danger' && 'text-danger',
        )}
      >
        {config.title}
      </h2>
      <p className="mx-auto mb-5 mt-1.5 max-w-[34ch] text-xs leading-relaxed text-muted">
        {config.body}
      </p>

      {result === 'genuine' ? (
        <Button full onClick={onDone}>
          Done
        </Button>
      ) : (
        <Button full variant="bad" icon="flag">
          Report this pack
        </Button>
      )}

      <Button full variant="grey" className="mt-2.5" onClick={onRetry}>
        Check another code
      </Button>
    </div>
  );
}

/** The scratch-panel illustration from the mockups. */
function ScratchPanelArt() {
  return (
    <div className="mb-[18px] grid h-[120px] place-items-center rounded-[10px] border border-line bg-blue-tint-2">
      <svg viewBox="0 0 200 100" aria-hidden className="h-[82%] w-[82%]">
        <rect x="20" y="16" width="160" height="68" rx="8" fill="#fff" stroke="#DCE3EE" strokeWidth="1.5" />
        <rect x="34" y="30" width="74" height="10" rx="3" fill="#DCE6F5" />
        <rect x="34" y="46" width="52" height="7" rx="3" fill="#EDF1F7" />
        <rect x="118" y="30" width="48" height="38" rx="5" fill="#B9BFC9" />
        <path d="M124 40 h36 M124 48 h30 M124 56 h34" stroke="#98A1AE" strokeWidth="2.5" strokeLinecap="round" />
        <path
          d="M140 22 q10 14 -4 26 q-12 10 2 24"
          fill="none"
          stroke="#0F52D9"
          strokeWidth="2.5"
          strokeDasharray="4 4"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
